import React, { useState, useEffect, useRef } from "react";
import { Navigate, useNavigate } from "react-router-dom";

import {
  doc,
  getDoc,
  setDoc,
  collection,
  getDocs,
  Timestamp,
} from "firebase/firestore";
import { FirebaseError } from "firebase/app";
import {
  updateProfile as updateAuthProfile,
} from "firebase/auth";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import "react-datepicker/dist/react-datepicker.css";
import "../components/DatePicker.css";

import { useAuth } from "../context/AuthContext";
import { db, storage } from "../firebase/config";
import AppShell from "../components/AppShell";
import { useUserProfile } from "../hooks/useUserProfile";
import { logOut as authServiceLogout } from "../services/authService";
import "./Dashboard.css";
import DeletePopup from "../components/DeletePopup";

const ChevronRight = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="20"
    height="20"
    viewBox="0 0 20 20"
    fill="none"
  >
    <path
      d="M7.5 15L12.5 10L7.5 5"
      stroke="#B3B3B3"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const NavCard: React.FC<{ label: string; onClick: () => void; className?: string }> = ({
  label,
  onClick,
  className = "",
}) => (
  <button
    type="button"
    onClick={onClick}
    className={[
      "flex flex-row justify-between items-center",
      "p-6 gap-[17px] w-full h-[58px]",
      "bg-white rounded-[16px] cursor-pointer shadow-sm active:scale-[0.98] transition-transform",
      "text-left",
      className,
    ]
      .filter(Boolean)
      .join(" ")}
  >
    <span className="text-[20px] font-semibold text-[#0C2624]">{label}</span>
    <div className="mr-[5px] w-[20px] h-[20px] flex-none">
      <ChevronRight />
    </div>
  </button>
);

// FIX: Interfaces to replace 'any' in calculateScore
interface LogEntry {
  weekNumber: number;
  symptoms?: Record<string, { level?: string; flow?: string }>;
  mood?: { key?: string; score?: number };
}

interface ActivityEntry {
  weekNumber: number;
  activities?: Record<string, boolean>;
}

const calculateScore = (logs: LogEntry[], acts: ActivityEntry[], checkins: string[]) => {
  let score = 15;
  for (let w = 1; w <= 6; w++) {
    if (w <= 5 && checkins.includes(`week_${w}`)) score += 5;
    const weekLogs = logs.filter((l) => l.weekNumber === w);
    score += Math.min(weekLogs.filter((l) => l.symptoms && Object.keys(l.symptoms).length > 0).length, 3);
    score += Math.min(weekLogs.filter((l) => l.mood && l.mood.key).length, 3);
    score += Math.min(weekLogs.filter((l) => l.symptoms?.vaginalBleeding).length, 3);
    const weekAct = acts.find((a) => a.weekNumber === w);
    if (weekAct && Object.values(weekAct.activities || {}).some((v) => v === true)) score += 1;
  }
  return Math.min(score, 100);
};

const Profile: React.FC = () => {
  const { user, loading } = useAuth();
  const { profile } = useUserProfile();
  const isMinimalNav =
    profile?.ftuPath === "antenatal" || profile?.ftuPath === "circumstances";
  const navigate = useNavigate();

  const [fullName, setFullName] = useState("");
  const [birthDate, setBirthDate] = useState<Date | null>(null);
  const [profileImageUrl, setProfileImageUrl] = useState<string | null>(null);

  // GP summary readiness
  const [readinessScore, setReadinessScore] = useState(15);
  const [dbConfirmedWeek, setDbConfirmedWeek] = useState<number | string>("-");
  const [showTooltip, setShowTooltip] = useState(false);

  const [, setSaving] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  // New state for deletion popup
  const [showDeletePopup, setShowDeletePopup] = useState(false);

  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const getMaxDate = () => {
    const today = new Date();
    return new Date(today.getFullYear() - 18, today.getMonth(), today.getDate());
  };

  // Load profile + GP summary readiness data
  useEffect(() => {
    let cancelled = false;

    async function loadProfile() {
      if (!user || !user.uid) return;

      try {
        const userRef = doc(db, "users", user.uid);
        const userSnap = await getDoc(userRef);

        if (cancelled || !user) return;

        if (userSnap.exists()) {
          const data = userSnap.data() as Record<string, unknown>;

          const firstNameFromDoc = typeof data.firstName === "string" ? data.firstName : undefined;
          const lastNameFromDoc = typeof data.lastName === "string" ? data.lastName : undefined;

          const fullNameFromDoc =
            (typeof data.fullName === "string" && data.fullName.trim().length > 0
              ? data.fullName
              : [firstNameFromDoc, lastNameFromDoc].filter(Boolean).join(" ")) || "";

          setFullName(fullNameFromDoc);

          if (data.birthDate instanceof Timestamp) {
            setBirthDate(data.birthDate.toDate());
          } else if (data.birthDate instanceof Date) {
            setBirthDate(data.birthDate);
          }

          if (typeof data.profileImageUrl === "string") {
            setProfileImageUrl(data.profileImageUrl);
          }
        }

        const progRef = doc(db, "users", user.uid, "recoveryProgrammes", "physical_wellbeing_6w");
        const progSnap = await getDoc(progRef);
        
        if (cancelled || !user) return;
        
        if (progSnap.exists()) {
          setDbConfirmedWeek(progSnap.data().confirmedWeek || 1);
        }

        const logsSnap = await getDocs(collection(db, "users", user.uid, "recoveryDailyLogs"));
        const actSnap = await getDocs(collection(db, "users", user.uid, "recoveryWeeklyActivities"));
        const checkinSnap = await getDocs(collection(db, "questionnaires", user.uid, "video_responses"));

        if (!cancelled && user) {
          setReadinessScore(
            calculateScore(
              logsSnap.docs.map((d) => d.data() as LogEntry),
              actSnap.docs.map((d) => d.data() as ActivityEntry),
              checkinSnap.docs.map((d) => d.id)
            )
          );
        }
      } catch (err) {
        if (!cancelled && user) {
          console.error("Error loading profile:", err);
          setError("Something went wrong while loading your profile.");
        }
      }
    }

    if (!loading && user) loadProfile();
    return () => { cancelled = true; };
  }, [user, loading]);

  useEffect(() => {
    if (!showTooltip) return;
    const dismiss = () => setShowTooltip(false);
    document.addEventListener("mousedown", dismiss);
    document.addEventListener("touchstart", dismiss);
    return () => {
      document.removeEventListener("mousedown", dismiss);
      document.removeEventListener("touchstart", dismiss);
    };
  }, [showTooltip]);

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!user) return;
    const file = e.target.files?.[0];
    if (!file) return;

    setError("");
    setSuccess("");
    setUploadingImage(true);

    try {
      const storageRef = ref(storage, `profileImages/${user.uid}`);
      await uploadBytes(storageRef, file);
      const url = await getDownloadURL(storageRef);
      setProfileImageUrl(url);
      await setDoc(doc(db, "users", user.uid), { profileImageUrl: url }, { merge: true });
      await updateAuthProfile(user, { photoURL: url });
      setSuccess("Profile photo updated.");
      setTimeout(() => setSuccess(""), 3000);
    } catch {
      setError("Could not upload your photo.");
    } finally {
      setUploadingImage(false);
      e.target.value = "";
    }
  };

  const handleProfileSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) return;

    setError("");
    setSuccess("");

    if (birthDate && birthDate > getMaxDate()) {
      setError("You must be at least 18 years old.");
      return;
    }

    setSaving(true);

    try {
      const trimmedFullName = fullName.trim();
      let firstName: string | null = null;
      let lastName: string | null = null;

      if (trimmedFullName.length > 0) {
        const parts = trimmedFullName.split(" ");
        firstName = parts[0];
        lastName = parts.slice(1).join(" ") || null;
      }

      await setDoc(
        doc(db, "users", user.uid),
        {
          fullName: trimmedFullName || null,
          firstName,
          lastName,
          birthDate: birthDate ?? null,
          profileImageUrl: profileImageUrl || null,
        },
        { merge: true }
      );

      if (trimmedFullName) {
        await updateAuthProfile(user, { displayName: trimmedFullName });
      }

      setSuccess("Your profile has been updated.");
    } catch (err) {
      console.error("Error updating profile:", err);
      if (err instanceof FirebaseError) {
        setError(err.message);
      } else {
        setError("Something went wrong while saving your profile.");
      }
    } finally {
      setSaving(false);
    }
  };

  const handleLogout = async () => {
    try {
      const result = await authServiceLogout();
      if (result.ok) {
        navigate("/login");
      } else {
        setError(result.error);
      }
    } catch (err) {
      console.error("Logout failed", err);
      setError("Could not log you out. Please try again.");
    }
  };

  // ── HIGH QUALITY BRANDED SPINNER ───────────────────────────────────────────
  if (loading) {
    return (
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        width: '100vw',
        height: '100%',
        minHeight: '-webkit-fill-available', 
        backgroundColor: '#fcf9f7',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 2147483647,
        pointerEvents: 'all'
      }}>
        {/* Spinner */}
        <div style={{
          width: '50px',
          height: '50px',
          border: '4px solid rgba(30, 94, 91, 0.1)',
          borderTop: '4px solid #1e5e5b',
          borderRadius: '50%',
          animation: 'spin 0.8s linear infinite',
          marginBottom: '20px',
          boxSizing: 'border-box'
        }} />

        {/* Branding */}
        <div style={{
          fontFamily: 'DM Sans, sans-serif',
          textAlign: 'center'
        }}>
          <p style={{
            color: '#0c2624',
            fontSize: '18px',
            fontWeight: '600',
            letterSpacing: '2px',
            textTransform: 'uppercase',
            margin: 0,
            animation: 'pulse 1.5s ease-in-out infinite'
          }}>
            BodyOwn
          </p>
        </div>

        <style>{`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
          @keyframes pulse {
            0%, 100% { opacity: 1; }
            50% { opacity: 0.5; }
          }
          body { overflow: hidden !important; }
        `}</style>
      </div>
    );
  }

  if (!user && !loading) return <Navigate to="/login" />;

  return (
    <AppShell title="Your profile" minimalNav={isMinimalNav}>
      <div className="relative w-full min-h-screen bg-[linear-gradient(167.14deg,#6C7A52_-17.94%,#DDE8C9_94.45%)]">

        <h1 className="absolute top-6 left-6 right-6 font-semibold text-[32px] text-white text-left">
          Account Profile
        </h1>

        <form onSubmit={handleProfileSave}>

          {(error || success) && (
            <div className="absolute top-[60px] left-6 right-6 z-10">
              {error && <p className="text-red-700 text-sm bg-white rounded-xl px-4 py-2">{error}</p>}
              {success && <p className="text-emerald-700 text-sm bg-white rounded-xl px-4 py-2">{success}</p>}
            </div>
          )}

          <div
            className="
              absolute top-[96px] left-0 w-full min-h-[calc(100vh-96px)]
              bg-[#FCF9F7] rounded-t-[32px]
              overflow-y-auto overflow-x-hidden
              [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]
              px-6 pt-10 pb-20
            "
          >
            <div className="flex flex-col items-center gap-8 w-full">

              <div className="relative flex flex-col items-center">
                <div className="relative w-[150px] h-[150px] flex items-center justify-center">
                  <svg
                    className="absolute w-full h-full transform -rotate-90"
                    viewBox="0 0 100 100"
                  >
                    <circle cx="50" cy="50" r="45" stroke="#EAEAEA" strokeWidth="5" fill="transparent" />
                    <circle
                      cx="50" cy="50" r="45"
                      stroke="#6C7A52" strokeWidth="5" fill="transparent"
                      strokeDasharray="283"
                      strokeDashoffset={283 - (283 * readinessScore) / 100}
                      strokeLinecap="round"
                      className="transition-all duration-1000 ease-out"
                    />
                  </svg>

                  <div className="relative w-[120px] h-[120px] rounded-full overflow-hidden border-2 border-white shadow-sm bg-[#E3E3E3]">
                    {profileImageUrl ? (
                      <img src={profileImageUrl} alt="Profile" className="w-full h-full object-cover" />
                    ) : (
                      <svg xmlns="http://www.w3.org/2000/svg" width="120" height="120" viewBox="0 0 120 120">
                        <circle cx="60" cy="60" r="60" fill="#E3E3E3" />
                        <circle cx="60" cy="49.2" r="20.4" fill="white" />
                        <circle cx="60" cy="114" r="38.4" fill="white" />
                      </svg>
                    )}
                    {uploadingImage && (
                      <div className="absolute inset-0 bg-black/30 flex items-center justify-center">
                        <span className="text-white text-sm">Uploading…</span>
                      </div>
                    )}
                  </div>

                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    className="absolute bottom-1 right-3 w-10 h-10 bg-white rounded-full shadow-md flex items-center justify-center border border-gray-100 z-10 transition-transform active:scale-90"
                  >
                    <svg width="18" height="18" viewBox="0 0 22 22" fill="none" stroke="#333" strokeWidth="2.1" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M16.05 1.878a2.121 2.121 0 013 3L7 19l-4 1 1-4L16.05 1.878z" />
                    </svg>
                  </button>

                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={handleImageUpload}
                  />
                </div>

                <div className="text-center mt-4">
                  <h2 className="text-[26px] font-bold text-[#0C2624]">{fullName || "—"}</h2>
                  <div className="flex items-center justify-center gap-1.5 mt-1.5">
                    <span className="text-[#6C7A52] font-bold text-[14px] uppercase tracking-[0.15em]">
                      Week {dbConfirmedWeek} · {readinessScore}% GP Summary
                    </span>
                    <button
                      type="button"
                      onMouseDown={(e) => e.stopPropagation()}
                      onTouchStart={(e) => e.stopPropagation()}
                      onClick={() => setShowTooltip((prev) => !prev)}
                      className="inline-flex items-center justify-center w-[16px] h-[16px] rounded-full border border-[#6C7A52] text-[#6C7A52] text-[10px] font-bold leading-none flex-shrink-0"
                    >
                      i
                    </button>
                  </div>
                </div>

                {showTooltip && (
                  <div
                    onMouseDown={(e) => e.stopPropagation()}
                    onTouchStart={(e) => e.stopPropagation()}
                    className="absolute top-full left-1/2 -translate-x-1/2 mt-3 z-50 w-[300px] p-5 bg-[#0C2624] text-white rounded-2xl shadow-2xl border border-white/5"
                  >
                    <div className="absolute -top-1.5 left-1/2 -translate-x-1/2 w-3 h-3 bg-[#0C2624] rotate-45 border-t border-l border-white/5" />
                    <h4 className="text-[14px] font-bold mb-2 text-[#DDE8C9]">
                      To complete your GP Summary:
                    </h4>
                    <p className="text-[12px] mb-3 text-white/80 leading-snug">
                      Your GP summary is built from your 6 weeks of tracking. Each week you should:
                    </p>
                    <ul className="text-[12px] space-y-2 font-medium">
                      <li className="flex items-start gap-2">
                        <span className="text-[#DDE8C9]">•</span>
                        <span>Log symptoms, mood & bleeding <strong>at least 3 times</strong></span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-[#DDE8C9]">•</span>
                        <span>Complete your <strong>weekly check-in</strong></span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-[#DDE8C9]">•</span>
                        <span>Complete <strong>at least 1 activity</strong></span>
                      </li>
                    </ul>
                    <p className="text-[11px] mt-4 pt-3 border-t border-white/10 text-white/50 italic">
                      The more you track, the more complete your summary will be.
                    </p>
                  </div>
                )}
              </div>

              <div className="flex flex-col gap-4 w-full">
                <NavCard
                  label="Personal details"
                  onClick={() => {
                    const parts = fullName.trim().split(/\s+/);
                    navigate("/profile/personal-details", {
                      state: {
                        firstName: parts[0] ?? "",
                        lastName: parts.slice(1).join(" ") ?? "",
                      },
                    });
                  }}
                />
                <NavCard label="Childbirth details" onClick={() => navigate("/profile/childbirth-details")} />
                <NavCard label="Appointments / Important dates" onClick={() => navigate("/profile/appointments")} />
                <NavCard label="Health profile" onClick={() => navigate("/profile/health-profile")} />
                <NavCard label="Recovery goals" onClick={() => navigate("/profile/recovery-goals")} />
                <NavCard label="Saved lessons" onClick={() => navigate("/profile/saved-lessons")} />
                <NavCard label="Change password" onClick={() => navigate("/profile/change-password")} />
                <NavCard label="Give feedback" onClick={() => navigate("/profile/feedback")} />
                <NavCard label="Propose an enhancement" onClick={() => navigate("/profile/feedback/suggestions")} />
                <NavCard label="Logout" onClick={handleLogout} className="block md:hidden" />
                
                <button
                  type="button"
                  onClick={() => setShowDeletePopup(true)}
                  className="w-full mt-4 flex flex-row justify-between items-center p-6 bg-[#F5F5F5] rounded-[16px] shadow-sm active:scale-[0.98] transition-transform text-left border border-gray-100"
                >
                  <span className="text-[20px] font-semibold text-[#6C7A52]">Delete Account</span>
                  <div className="mr-[5px] w-[20px] h-[20px] flex-none">
                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
                      <path 
                        d="M7.5 15L12.5 10L7.5 5" 
                        stroke="#6C7A52" 
                        strokeWidth="2" 
                        strokeLinecap="round" 
                        strokeLinejoin="round" 
                      />
                    </svg>
                  </div>
                </button>
              </div>

            </div>
          </div>
        </form>
      </div>

      {showDeletePopup && (
        <DeletePopup 
          user={user} 
          onClose={() => setShowDeletePopup(false)} 
        />
      )}
    </AppShell>
  );
};

export default Profile;